import { useLingui } from "@lingui/react/macro";
import { LayoutList, FileText, Rows3, Table2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { RecipeViewMode } from "@/hooks/recipes/useRecipeViewMode";

interface RecipeViewModeToggleProps {
  value: RecipeViewMode;
  onChange: (mode: RecipeViewMode) => void;
}

/**
 * Detailed (the tabbed view) or Card (the one a cook prints) — T-806.
 * Chrome, so it does not print.
 */
export function RecipeViewModeToggle({ value, onChange }: RecipeViewModeToggleProps) {
  const { t } = useLingui();

  return (
    <div
      role="group"
      aria-label={t`Recipe view`}
      className="flex w-fit max-w-full flex-wrap items-center gap-1 rounded-lg border border-(--color-border) bg-(--color-surface-muted) p-1 print:hidden"
    >
      <ModeButton
        label={t`Detailed`}
        icon={<LayoutList className="size-4" />}
        isActive={value === "detailed"}
        onClick={() => onChange("detailed")}
      />
      <ModeButton
        label={t`Card`}
        icon={<FileText className="size-4" />}
        isActive={value === "card"}
        onClick={() => onChange("card")}
      />
      <ModeButton
        label={t`Simple`}
        icon={<Table2 className="size-4" />}
        isActive={value === "simple"}
        onClick={() => onChange("simple")}
      />
      <ModeButton
        label={t`Staged`}
        icon={<Rows3 className="size-4" />}
        isActive={value === "staged"}
        onClick={() => onChange("staged")}
      />
    </div>
  );
}

function ModeButton({
  label,
  icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
        isActive
          ? "bg-(--color-surface-content) text-(--color-text-primary) shadow-sm"
          : "text-(--color-text-secondary) hover:text-(--color-text-primary)",
      )}
    >
      {icon}
      {label}
    </button>
  );
}
